import { useMemo, useState } from 'react';
import { verifyApi } from '../api/scans.ts';

interface BadgeEmbedSectionProps {
  verificationToken: string;
  score: number;
  tier: 'Bronze' | 'Silver' | 'Gold';
  badgeIssued: boolean;
}

// Renders the "Get your compliance badge" panel on the scan results page when the scan
// qualifies (score >= 80). Shows a live preview of the badge SVG, a copy-paste embed
// snippet, and a link to the public verifier page.
//
// The embed snippet uses ABSOLUTE URLs (window.location.origin) so it works when pasted
// into any external site. Defaults to https://eaaproof.com if no origin is available
// (e.g. SSR / prerender).
export default function BadgeEmbedSection({ verificationToken, score, tier, badgeIssued }: BadgeEmbedSectionProps) {
  const [copied, setCopied] = useState(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://eaaproof.com';
  const verifierUrl = verifyApi.verifierUrl(verificationToken, origin);
  const badgeImgUrl = `${origin}${verifyApi.badgeImageUrl(verificationToken)}`;

  const snippet = useMemo(
    () => `<a href="${verifierUrl}" target="_blank" rel="noopener">
  <img src="${badgeImgUrl}"
       alt="EAA Proof compliance score ${score}/100 — ${tier}"
       width="200" height="80" loading="lazy">
</a>`,
    [verifierUrl, badgeImgUrl, score, tier]
  );

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Clipboard API may be blocked in some embedded contexts. The user can still select + copy.
    }
  }

  // Badge auto-issues at scan completion, but if it ever failed (transient R2 outage)
  // the UI shows a "your badge is being prepared" hint instead of broken image.
  if (!badgeIssued) {
    return (
      <section className="badge-embed pending" aria-labelledby="badge-heading">
        <h2 id="badge-heading">Your compliance badge</h2>
        <p className="muted">
          Your badge is being prepared. Refresh in a few seconds — if it doesn't appear,
          email <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>.
        </p>
      </section>
    );
  }

  return (
    <section className="badge-embed" aria-labelledby="badge-heading">
      <h2 id="badge-heading">Your compliance badge</h2>
      <p>
        You scored <strong>{score}/100</strong> — qualifies for the <strong>{tier}</strong> tier.
        Embed this badge on your website to show visitors (and AI crawlers) that you've been
        audited. The badge links back to a public verifier page hosted on eaaproof.com.
      </p>

      <div className="badge-preview" aria-label="Badge preview">
        <img
          src={verifyApi.badgeImageUrl(verificationToken)}
          alt={`EAA Proof compliance score ${score}/100 — ${tier}`}
          width={200}
          height={80}
        />
      </div>

      <div className="badge-snippet">
        <label htmlFor="badge-snippet-textarea">Copy this HTML into your site</label>
        <textarea
          id="badge-snippet-textarea"
          readOnly
          rows={5}
          value={snippet}
          onClick={e => (e.target as HTMLTextAreaElement).select()}
        />
        <button type="button" className="cta secondary" onClick={handleCopy}>
          {copied ? 'Copied ✓' : 'Copy embed code'}
        </button>
        <a
          className="cta tertiary"
          href={verifierUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Preview verifier page →
        </a>
      </div>
    </section>
  );
}
